// src/components/Dashboard/CreateClaimRecord.jsx
//
// Paso 1 de la restructuración (ver memoria del proyecto:
// claim_lifecycle_restructure.md): intake del reclamo SIN nada financiero.
// Reutiliza el patrón de modales de Compañía/Ajustador/Causa de
// CreateClaim.jsx, pero es un componente propio porque este formulario ya
// no comparte casi ningún campo con el viejo (sin monto, %, invoice,
// payableTo). Al guardar, entrega el claimRecord recién creado al padre
// para encadenar inmediatamente el Closing Statement (Paso 2).
import React, { useState, useContext, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { LanguageContext } from '../../App';
import { getGlobalSettings, saveGlobalSettings } from '../../services/claimService';
import { getAdjusters } from '../../services/adjusterService';
import { createClaimRecord, updateClaimRecord } from '../../services/claimRecordService';
import ConfirmModal from '../common/ConfirmModal';
import { assistantsFor, isAssistant, isCsrStaff } from '../../utils/claimAccess';
import useStatusCatalog from '../../hooks/useStatusCatalog';
import { draftKey, readDraft, writeDraft, removeDraft } from '../../utils/drafts';
import './CreateClaim.css';

const emptyOwner = { firstName: '', lastName: '', phone: '', email: '' };
const US_STATES = ['FL', 'GA', 'AL', 'SC', 'NC', 'TX', 'LA', 'MS', 'TN', 'OTHER'];

// existingRecord: cuando viene seteado, este mismo formulario edita ese
// claimRecord (updateClaimRecord) en vez de crear uno nuevo — reutilizado
// por el wizard de Create (QuickCreate.jsx) cuando se retoma el Paso 1 de
// un reclamo ya existente, para que TODA edición pase por el mismo panel.
const CreateClaimRecord = ({ onCreated, existingRecord }) => {
  const statusCatalog = useStatusCatalog();
  const { language } = useContext(LanguageContext);
  const { currentUser, isAdmin, userCompany, userAdjusterName, userCanViewAllClaims, userRoleKey, userCompanies } = useAuth();
  // CSR Assistant (varias compañías) y P.A Admin crean reclamos para cualquier P.A de su compañía.
  const isCsr = userRoleKey === 'csr_assistant';
  const canPickAdjuster = isAdmin || isCsr || userRoleKey === 'pa_admin';
  // Quién entra al reclamo lo decide el equipo CSR o el supervisor de la empresa (firestore.rules).
  // Al crear, el ajustador también puede agregar otros PA y Other Adjuster Access;
  // al editar, solo lo cambia el equipo CSR o el supervisor.
  const canEditAccess = isAdmin || userCanViewAllClaims || !existingRecord;
  // El asistente de un P.A no es el P.A principal: elige entre los P.A a los que está enlazado.
  const isPaAssistant = userRoleKey === 'pa_assistant';
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [companiesList, setCompaniesList] = useState(['Adjustify']);
  const [adjustersList, setAdjustersList] = useState([]);
  const [causesList, setCausesList] = useState(['Water Damage', 'Fire Damage', 'Storm Damage', 'Theft', 'Other']);
  const [showCauseConfigModal, setShowCauseConfigModal] = useState(false);
  const [editingCause, setEditingCause] = useState(null);
  const [newCauseName, setNewCauseName] = useState('');
  const [pendingDeleteCause, setPendingDeleteCause] = useState(null);

  // El ajustador solo puede crear/editar reclamos de SÍ MISMO, dentro de su
  // propia empresa (ver firestore.rules) — acá se bloquea desde la UI para
  // que ni siquiera pueda intentarlo (en vez de dejar que Firestore lo
  // rechace después de llenar todo el formulario).
  // Borrador del alta (solo al crear, no al editar): se guarda en sessionStorage
  // por usuario mientras se llena, se recupera al volver y se borra al crear,
  // descartar o cerrar sesión. Cubre cambiar de sección y Atrás; recargar
  // conserva el borrador y además avisa (beforeunload).
  const draftStorageKey = existingRecord ? null : draftKey('new-claim', currentUser?.uid);
  const initialDraft = useRef(readDraft(draftStorageKey));
  const [restoredDraft, setRestoredDraft] = useState(initialDraft.current);
  const buildInitialForm = () => ({
    claimNumber: existingRecord?.claimNumber || '',
    oaStatus: existingRecord?.oaStatus || '',
    clientName: existingRecord?.clientName || '',
    companyName: existingRecord?.companyName || (isAdmin ? 'Adjustify' : isCsr ? (userCompanies[0] || '') : userCompany || ''),
    adjusterName: existingRecord?.adjusterName || (isAdmin || isCsr || isPaAssistant ? '' : userAdjusterName || ''),
    incidentDate: existingRecord?.incidentDate || '',
    policyNumber: existingRecord?.policyNumber || '',
    cause: existingRecord?.cause || '',
    crmClaimId: existingRecord?.crmClaimId || '',
    // Aseguradora que emite el cheque — distinta de `companyName` (la
    // empresa de ajuste/P.A. que representa al cliente).
    insuranceCompanyName: existingRecord?.insuranceCompanyName || '',
    // "Cliente y P.A." — vivía en el Paso 2 (Closing Statement), se movió
    // acá para no pedir los mismos datos dos veces (ver
    // claim_lifecycle_restructure.md, simplificación previa al envío a firmar).
    clientEmail: existingRecord?.clientEmail || '',
    clientPhone: existingRecord?.clientPhone || '',
    // Dirección del siniestro/propiedad (Property Address en el export de
    // Organized Adjusting) — ver OASync.jsx para el llenado masivo vía Excel.
    clientAddress: existingRecord?.clientAddress || '',

    // --- Campos nuevos (spec del cliente 2026-09-17) ---
    propertyType: existingRecord?.propertyType || '',
    owner2: existingRecord?.owner2 || emptyOwner,
    owner3: existingRecord?.owner3 || emptyOwner,
    lossAddressStreet: existingRecord?.lossAddressStreet || '',
    lossAddressCity: existingRecord?.lossAddressCity || '',
    lossAddressState: existingRecord?.lossAddressState || '',
    lossAddressZip: existingRecord?.lossAddressZip || '',
    mailingSameAsLoss: existingRecord?.mailingSameAsLoss !== false,
    mailingAddressStreet: existingRecord?.mailingAddressStreet || '',
    mailingAddressCity: existingRecord?.mailingAddressCity || '',
    mailingAddressState: existingRecord?.mailingAddressState || '',
    mailingAddressZip: existingRecord?.mailingAddressZip || '',
    insuranceClaimEmail: existingRecord?.insuranceClaimEmail || '',
    insurancePhone: existingRecord?.insurancePhone || '',
    agentName: existingRecord?.agentName || '',
    agentAddress: existingRecord?.agentAddress || '',
    agentPhone: existingRecord?.agentPhone || '',
    insuranceAdjusterName: existingRecord?.insuranceAdjusterName || '',
    insuranceAdjusterPhone: existingRecord?.insuranceAdjusterPhone || '',
    insuranceAdjusterEmail: existingRecord?.insuranceAdjusterEmail || '',
    coverageA: existingRecord?.coverageA ?? '',
    coverageB: existingRecord?.coverageB ?? '',
    coverageC: existingRecord?.coverageC ?? '',
    coverageD: existingRecord?.coverageD ?? '',
    deductibleAmount: existingRecord?.deductibleAmount ?? '',
    recoverableDepreciation: existingRecord?.recoverableDepreciation ?? '',
    deductibleType: existingRecord?.deductibleType || 'fixed',
    collaboratorAdjusters: existingRecord?.collaboratorAdjusters || [],
    otherAccess: existingRecord?.otherAccess || [],
    attorneyName: existingRecord?.attorneyName || '',
    contractSignedDate: existingRecord?.contractSignedDate || '',
    dateCalledIn: existingRecord?.dateCalledIn || '',
    vendor: existingRecord?.vendor || '',
    mortgageCompanyName: existingRecord?.mortgageCompanyName || '',
    mortgageLoanNumber: existingRecord?.mortgageLoanNumber || '',
    mortgagePhone: existingRecord?.mortgagePhone || '',

    // --- Reunión 22-sep + LOR/SPOL (ver ANALISIS_DOCUMENTOS_LOR_SPOL.md) ---
    agreedFeePercent: existingRecord?.agreedFeePercent ?? '',
    clientMobile: existingRecord?.clientMobile || '',
    commercialEntityName: existingRecord?.commercialEntityName || '',
    commercialEntityEmail: existingRecord?.commercialEntityEmail || '',
    commercialEntityPhone: existingRecord?.commercialEntityPhone || '',
    lossTime: existingRecord?.lossTime || '',
    lossDescription: existingRecord?.lossDescription || '',
    claimType: existingRecord?.claimType || '',
    policyEffectiveDate: existingRecord?.policyEffectiveDate || '',
    policyExpirationDate: existingRecord?.policyExpirationDate || '',
    agentAgency: existingRecord?.agentAgency || '',
    insuranceCompanyAddress: existingRecord?.insuranceCompanyAddress || '',
    occupancy: existingRecord?.occupancy || '',
    insuredInterest: existingRecord?.insuredInterest || '',
    deskAdjusterName: existingRecord?.deskAdjusterName || '',
    deskAdjusterPhone: existingRecord?.deskAdjusterPhone || '',
    deskAdjusterEmail: existingRecord?.deskAdjusterEmail || '',
    leadSource: existingRecord?.leadSource || '',
    signatureObtainedBy: existingRecord?.signatureObtainedBy || '',
    negotiator: existingRecord?.negotiator || '',
    mitigationCompany: existingRecord?.mitigationCompany || '',
    additionalContacts: existingRecord?.additionalContacts || []
  });
  const [formData, setFormData] = useState(() => initialDraft.current?.formData || buildInitialForm());
  const [touched, setTouched] = useState(false);

  const [showOwner2, setShowOwner2] = useState(!!(existingRecord?.owner2?.firstName || existingRecord?.owner2?.lastName) || !!initialDraft.current?.showOwner2);
  const [showOwner3, setShowOwner3] = useState(!!(existingRecord?.owner3?.firstName || existingRecord?.owner3?.lastName) || !!initialDraft.current?.showOwner3);
  // Paso 2 del alta (póliza y seguro): se puede completar después — abierto
  // al editar un reclamo existente, cerrado al crear uno nuevo.
  const [showPolicySection, setShowPolicySection] = useState(Boolean(existingRecord));
  // Los datos opcionales van plegados para que lo obligatorio quede a la vista;
  // arrancan abiertos al editar o cuando ya traen datos (p. ej. un borrador
  // recuperado). Ninguno contiene campos obligatorios, así que la validación
  // nativa del formulario no se ve afectada.
  const hasContacts = (formData.additionalContacts || []).length > 0;
  const hasExtras = Boolean(existingRecord)
    || [formData.attorneyName, formData.vendor, formData.dateCalledIn, formData.contractSignedDate].some(Boolean)
    || (formData.collaboratorAdjusters || []).length > 0 || (formData.otherAccess || []).length > 0;
  const [openContacts, setOpenContacts] = useState(hasContacts);
  const [openExtras, setOpenExtras] = useState(hasExtras);
  // Si los datos llegan después de montar (borrador o registro que carga tarde) el grupo se abre;
  // nunca se cierra solo, para no quitarle al usuario lo que abrió.
  useEffect(() => { if (hasContacts) setOpenContacts(true); }, [hasContacts]);
  useEffect(() => { if (hasExtras) setOpenExtras(true); }, [hasExtras]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [settings, allAdjusters] = await Promise.all([getGlobalSettings(), getAdjusters()]);
        setAdjustersList(allAdjusters);
        let defaultCause = existingRecord?.cause || 'Water Damage';
        if (settings?.causesList?.length) { setCausesList(settings.causesList); if (!existingRecord) defaultCause = settings.causesList[0]; }

        if (isAdmin || isCsr) {
          let defaultCompany = existingRecord?.companyName || (isCsr ? userCompanies[0] : 'Adjustify') || 'Adjustify';
          const allowedCompanies = isCsr ? userCompanies : settings?.companiesList;
          if (allowedCompanies?.length) { setCompaniesList(allowedCompanies); if (!existingRecord) defaultCompany = allowedCompanies[0]; }
          // Al editar, no pisar el ajustador ya guardado con el primero
          // disponible — solo completar un valor por defecto si no hay ninguno.
          if (!existingRecord && !initialDraft.current) {
            const availableAdjusters = allAdjusters.filter(a => a.company === defaultCompany);
            setFormData(prev => ({ ...prev, companyName: defaultCompany, adjusterName: availableAdjusters[0]?.name || '', cause: defaultCause }));
          } else {
            setFormData(prev => ({ ...prev, cause: prev.cause || defaultCause }));
          }
        } else if (!existingRecord && !initialDraft.current) {
          // El ajustador siempre crea para sí mismo/su empresa — ya viene
          // seteado en el estado inicial de formData, solo falta la causa.
          const linkedPas = allAdjusters.find(a => a.name === userAdjusterName)?.assistantOf || [];
          setFormData(prev => ({ ...prev, cause: defaultCause, ...(isPaAssistant ? { adjusterName: linkedPas[0] || '' } : {}) }));
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Los asistentes no son PA: no se asignan como PA ni cobran comisión.
  const getAdjustersForCompany = companyName => adjustersList.filter(a => a.company === companyName && !isAssistant(a) && !isCsrStaff(a)).map(a => a.name);
  const getAdjustersForCompanyObjects = companyName => adjustersList.filter(a => a.company === companyName && !isAssistant(a) && !isCsrStaff(a));

  // P.A que se pueden elegir como principal: los de la compañía, o (asistente) solo los enlazados a él.
  const pickableAdjusters = isPaAssistant
    ? (adjustersList.find(a => a.name === userAdjusterName)?.assistantOf || [])
    : getAdjustersForCompany(formData.companyName);

  useEffect(() => {
    // El ajustador tiene compañía/ajustador fijos (ver arriba) — este
    // auto-selector de "primer ajustador disponible" es solo para el panel
    // admin, que sí puede cambiar de empresa libremente.
    if (initialLoading || !canPickAdjuster) return;
    const available = getAdjustersForCompany(formData.companyName);
    if (available.length > 0) {
      if (!available.includes(formData.adjusterName)) setFormData(prev => ({ ...prev, adjusterName: available[0] }));
    } else {
      setFormData(prev => ({ ...prev, adjusterName: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.companyName, canPickAdjuster]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleOwnerChange = (ownerKey, field, value) => {
    setFormData(prev => ({ ...prev, [ownerKey]: { ...prev[ownerKey], [field]: value } }));
  };

  const setContact = (index, field, value) => setFormData(prev => ({
    ...prev,
    additionalContacts: prev.additionalContacts.map((c, i) => (i === index ? { ...c, [field]: value } : c))
  }));
  const addContact = () => setFormData(prev => ({ ...prev, additionalContacts: [...prev.additionalContacts, { name: '', relation: '', phone: '', email: '' }] }));
  const removeContact = index => setFormData(prev => ({ ...prev, additionalContacts: prev.additionalContacts.filter((_, i) => i !== index) }));

  const toggleIn = (key, name) => {
    setFormData(prev => {
      const has = prev[key].includes(name);
      return { ...prev, [key]: has ? prev[key].filter(a => a !== name) : [...prev[key], name] };
    });
  };
  const toggleCollaborator = name => toggleIn('collaboratorAdjusters', name);

  // Gestión de causas (agregar/editar/borrar) — este formulario nuevo había
  // perdido el modal de "Configurar Causas" que sí tenía el viejo
  // CreateClaim.jsx, dejando el select de Causa sin forma de agregar una
  // que no estuviera ya en la lista por defecto.
  const handleAddCause = async () => {
    const name = newCauseName.trim();
    if (!name || causesList.includes(name)) return;
    const updatedCauses = [...causesList, name];
    setCausesList(updatedCauses);
    setNewCauseName('');
    setFormData(prev => ({ ...prev, cause: name }));
    try {
      await saveGlobalSettings({ causesList: updatedCauses });
    } catch (error) {
      console.error('Error saving causes to Firebase:', error);
    }
  };

  const handleSaveCauseEdit = async () => {
    const name = newCauseName.trim();
    if (!editingCause || !name) return;
    const updatedCauses = causesList.map(c => c === editingCause ? name : c);
    setCausesList(updatedCauses);
    if (formData.cause === editingCause) setFormData(prev => ({ ...prev, cause: name }));
    setEditingCause(null);
    setNewCauseName('');
    try {
      await saveGlobalSettings({ causesList: updatedCauses });
    } catch (error) {
      console.error('Error saving causes to Firebase:', error);
    }
  };

  const handleDeleteCause = causeName => {
    if (causesList.length <= 1) return;
    setPendingDeleteCause(causeName);
  };

  const confirmDeleteCause = async () => {
    const causeName = pendingDeleteCause;
    setPendingDeleteCause(null);
    if (!causeName) return;
    const updatedCauses = causesList.filter(c => c !== causeName);
    setCausesList(updatedCauses);
    if (formData.cause === causeName) setFormData(prev => ({ ...prev, cause: updatedCauses[0] || '' }));
    try {
      await saveGlobalSettings({ causesList: updatedCauses });
    } catch (error) {
      console.error('Error saving causes to Firebase:', error);
    }
  };

  // Autoguardado del borrador mientras hay cambios sin enviar.
  useEffect(() => {
    if (!draftStorageKey || !touched) return;
    writeDraft(draftStorageKey, { formData, showOwner2, showOwner3, showPolicySection, savedAt: Date.now() });
  }, [draftStorageKey, touched, formData, showOwner2, showOwner3, showPolicySection]);
  // Recargar o cerrar la pestaña con cambios sin enviar: el navegador avisa.
  useEffect(() => {
    if (!draftStorageKey || !touched) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [draftStorageKey, touched]);
  const discardDraft = () => {
    removeDraft(draftStorageKey);
    initialDraft.current = null;
    setRestoredDraft(null);
    setTouched(false);
    setShowOwner2(false); setShowOwner3(false); setShowPolicySection(false);
    setFormData(buildInitialForm());
  };

  const handleSubmit = async e => {
    e.preventDefault();
    // El Claim # NO es obligatorio: lo asigna la aseguradora cuando se reporta
    // el caso (reunión 22-sep). La póliza tampoco: va en el segundo paso.
    if (!formData.clientName || !formData.companyName || !formData.adjusterName || !formData.cause) {
      toast.error(language === 'es' ? 'Completa los campos obligatorios' : 'Complete the required fields');
      return;
    }
    // `clientAddress` (texto libre) sigue siendo lo que leen cheques, Claim
    // Status y OASync: se arma desde la dirección estructurada cuando existe.
    const composedAddress = [formData.lossAddressStreet, formData.lossAddressCity, [formData.lossAddressState, formData.lossAddressZip].filter(Boolean).join(' ')]
      .map(part => (part || '').trim()).filter(Boolean).join(', ');
    const data = { ...formData, clientAddress: formData.lossAddressStreet ? composedAddress : formData.clientAddress };
    setLoading(true);
    try {
      if (existingRecord) {
        await updateClaimRecord(existingRecord.id, data);
        toast.success(language === 'es' ? 'Reclamo actualizado' : 'Claim updated');
        if (onCreated) onCreated({ ...existingRecord, ...data });
      } else {
        const record = await createClaimRecord(data, currentUser.email, isAdmin ? 'admin' : isCsr ? 'csr_assistant' : 'adjuster');
        removeDraft(draftStorageKey);
        setTouched(false);
        toast.success(language === 'es' ? 'Reclamo creado' : 'Claim created');
        if (onCreated) onCreated(record);
      }
    } catch (error) {
      console.error(error);
      toast.error(existingRecord
        ? (language === 'es' ? 'Error al guardar el reclamo' : 'Error saving claim')
        : (language === 'es' ? 'Error al crear el reclamo' : 'Error creating claim'));
    } finally {
      setLoading(false);
    }
  };

  // Bloque de un asegurado adicional (owner2/owner3): nombre completo,
  // teléfono y correo. El nombre se guarda entero en firstName (lastName
  // queda vacío); los datos viejos con nombre y apellido separados se
  // muestran juntos.
  const ownerFullName = owner => `${owner.firstName || ''} ${owner.lastName || ''}`.trim();
  const renderOwner = (ownerKey, title, onRemove) => (
    <div className="cc-person-card">
      <div className="cc-person-card-title">
        <i className="fa-solid fa-user"></i> {title}
        <button type="button" className="cc-owner-remove" onClick={onRemove}>
          <i className="fa-solid fa-xmark"></i> {language === 'es' ? 'Quitar' : 'Remove'}
        </button>
      </div>
      <div className="cc-grid">
        <div className="cc-input-group">
          <label className="cc-label" htmlFor={`${ownerKey}-name`}>{language === 'es' ? 'Nombre completo' : 'Full name'}</label>
          <input id={`${ownerKey}-name`} type="text" className="cc-input" value={ownerFullName(formData[ownerKey])}
            onChange={e => setFormData(prev => ({ ...prev, [ownerKey]: { ...prev[ownerKey], firstName: e.target.value, lastName: '' } }))} />
        </div>
        <div className="cc-input-group">
          <label className="cc-label" htmlFor={`${ownerKey}-phone`}>{language === 'es' ? 'Teléfono' : 'Phone'}</label>
          <input id={`${ownerKey}-phone`} type="tel" className="cc-input" value={formData[ownerKey].phone} onChange={e => handleOwnerChange(ownerKey, 'phone', e.target.value)} />
        </div>
        <div className="cc-input-group">
          <label className="cc-label" htmlFor={`${ownerKey}-email`}>{language === 'es' ? 'Correo' : 'Email'}</label>
          <input id={`${ownerKey}-email`} type="email" className="cc-input" value={formData[ownerKey].email} onChange={e => handleOwnerChange(ownerKey, 'email', e.target.value)} />
        </div>
      </div>
    </div>
  );

  if (initialLoading) {
    return (
      <div className="cc-loading-wrapper">
        <i className="fa-solid fa-spinner fa-spin"></i>
        <p>{language === 'es' ? 'Cargando configuraciones...' : 'Loading configuration...'}</p>
      </div>
    );
  }

  return (
    <div className="cc-wrapper">
      <div className="cc-header">
        <div className="cc-header-left">
          <h2><i className="fa-solid fa-folder-plus"></i> {existingRecord
            ? (language === 'es' ? 'Editar datos del reclamo' : 'Edit claim details')
            : (language === 'es' ? 'Datos del reclamo' : 'Claim details')}</h2>
          <p>{language === 'es' ? 'Primero los datos básicos del caso; la póliza se puede completar después.' : 'Basic case facts first; policy details can be completed later.'}</p>
        </div>
      </div>

      {restoredDraft && (
        <div className="cc-draft-banner" role="status">
          <span>{language === 'es'
            ? `Recuperamos tu borrador sin guardar${restoredDraft.savedAt ? ` (${new Date(restoredDraft.savedAt).toLocaleString('es', { dateStyle: 'short', timeStyle: 'short' })})` : ''}. Revísalo y crea el reclamo, o descártalo.`
            : `We restored your unsaved draft${restoredDraft.savedAt ? ` (${new Date(restoredDraft.savedAt).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })})` : ''}. Review it and create the claim, or discard it.`}</span>
          <button type="button" className="cd-btn" onClick={discardDraft}>{language === 'es' ? 'Descartar borrador' : 'Discard draft'}</button>
        </div>
      )}
      <form onSubmit={handleSubmit} onInput={() => setTouched(true)} className="cc-form"
        // Un campo inválido dentro de un grupo opcional plegado no se podría enfocar y el envío
        // se bloquearía sin decir por qué: se abre el grupo para que el navegador lo señale.
        onInvalidCapture={event => { const group = event.target.closest?.('details.cc-optional'); if (group && !group.open) group.open = true; }}>
        {/* ── PASO 1: DATOS BÁSICOS (lo único obligatorio para crear el reclamo) ── */}
        <h3 className="cc-section-title"><i className="fa-solid fa-house"></i> {language === 'es' ? '1. Datos básicos' : '1. Basic details'}</h3>

        <div className="cc-grid">
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-propertyType"><i className="fa-solid fa-building-user"></i> {language === 'es' ? 'Residencial / Comercial' : 'Residential / Commercial'}</label>
            <select id="intake-propertyType" name="propertyType" value={formData.propertyType} onChange={handleChange} className="cc-select">
              <option value="">{language === 'es' ? 'Sin especificar' : 'Unspecified'}</option>
              <option value="residential">{language === 'es' ? 'Residencial' : 'Residential'}</option>
              <option value="commercial">{language === 'es' ? 'Comercial' : 'Commercial'}</option>
            </select>
          </div>
        </div>

        {formData.propertyType === 'commercial' && (
          <div className="cc-person-card">
            <div className="cc-person-card-title"><i className="fa-solid fa-building"></i> {language === 'es' ? 'Compañía asegurada' : 'Insured company'}</div>
            <div className="cc-grid">
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-commercialEntityName">{language === 'es' ? 'Nombre legal (LLC, Corp.)' : 'Legal name (LLC, Corp.)'}</label>
                <input id="intake-commercialEntityName" type="text" name="commercialEntityName" value={formData.commercialEntityName} onChange={handleChange} className="cc-input" />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-commercialEntityPhone">{language === 'es' ? 'Teléfono' : 'Phone'}</label>
                <input id="intake-commercialEntityPhone" type="tel" name="commercialEntityPhone" value={formData.commercialEntityPhone} onChange={handleChange} className="cc-input" />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-commercialEntityEmail">{language === 'es' ? 'Correo' : 'Email'}</label>
                <input id="intake-commercialEntityEmail" type="email" name="commercialEntityEmail" value={formData.commercialEntityEmail} onChange={handleChange} className="cc-input" />
              </div>
            </div>
          </div>
        )}

        <p className="cc-subsection-label">{language === 'es' ? 'Propiedad afectada' : 'Loss property'}</p>
        <div className="cc-grid">
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-lossAddressStreet">{language === 'es' ? 'Calle' : 'Street'}</label>
            <input id="intake-lossAddressStreet" type="text" name="lossAddressStreet" className="cc-input" value={formData.lossAddressStreet} onChange={handleChange} />
          </div>
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-lossAddressCity">{language === 'es' ? 'Ciudad' : 'City'}</label>
            <input id="intake-lossAddressCity" type="text" name="lossAddressCity" className="cc-input" value={formData.lossAddressCity} onChange={handleChange} />
          </div>
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-lossAddressState">{language === 'es' ? 'Estado' : 'State'}</label>
            <select id="intake-lossAddressState" name="lossAddressState" className="cc-select" value={formData.lossAddressState} onChange={handleChange}>
              <option value="">—</option>
              {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-lossAddressZip">{language === 'es' ? 'Código postal' : 'Zip code'}</label>
            <input id="intake-lossAddressZip" type="text" name="lossAddressZip" className="cc-input" value={formData.lossAddressZip} onChange={handleChange} />
          </div>
        </div>
        {formData.clientAddress && !formData.lossAddressStreet && (
          <p className="cc-subsection-label">{language === 'es' ? `Dirección anterior (texto libre): ${formData.clientAddress}` : `Previous address (free text): ${formData.clientAddress}`}</p>
        )}

        <p className="cc-subsection-label">{formData.propertyType === 'commercial'
          ? (language === 'es' ? 'Persona de contacto de la compañía' : 'Company contact person')
          : (language === 'es' ? 'Asegurados' : 'Insureds')}</p>

        {/* Asegurado 1 (o contacto de la compañía): nombre, contacto y dirección postal en un solo bloque */}
        <div className="cc-person-card">
          <div className="cc-person-card-title"><i className="fa-solid fa-user"></i> {formData.propertyType === 'commercial'
            ? (language === 'es' ? 'Contacto' : 'Contact')
            : (language === 'es' ? 'Asegurado 1' : 'Insured 1')}</div>
          <div className="cc-grid">
            <div className="cc-input-group">
              <label className="cc-label" htmlFor="intake-clientName">{language === 'es' ? 'Nombre completo' : 'Full name'} *</label>
              <input id="intake-clientName" type="text" name="clientName" value={formData.clientName} onChange={handleChange} required className="cc-input" />
            </div>
            <div className="cc-input-group">
              <label className="cc-label" htmlFor="intake-clientPhone">{language === 'es' ? 'Teléfono' : 'Phone'}</label>
              <input id="intake-clientPhone" type="tel" name="clientPhone" value={formData.clientPhone} onChange={handleChange} className="cc-input" />
            </div>
            <div className="cc-input-group">
              <label className="cc-label" htmlFor="intake-clientMobile">{language === 'es' ? 'Celular' : 'Mobile'}</label>
              <input id="intake-clientMobile" type="tel" name="clientMobile" value={formData.clientMobile} onChange={handleChange} className="cc-input" />
            </div>
            <div className="cc-input-group">
              <label className="cc-label" htmlFor="intake-clientEmail">{language === 'es' ? 'Correo' : 'Email'}</label>
              <input id="intake-clientEmail" type="email" name="clientEmail" value={formData.clientEmail} onChange={handleChange} className="cc-input" />
            </div>
          </div>
          <label className="cc-checkbox-row">
            <input type="checkbox" name="mailingSameAsLoss" checked={formData.mailingSameAsLoss} onChange={handleChange} />
            {language === 'es' ? 'Recibe el correo postal en la propiedad afectada' : 'Receives mail at the loss property'}
          </label>
          {!formData.mailingSameAsLoss && (
            <div className="cc-grid">
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-mailingAddressStreet">{language === 'es' ? 'Dirección postal' : 'Mailing street'}</label>
                <input id="intake-mailingAddressStreet" type="text" name="mailingAddressStreet" className="cc-input" value={formData.mailingAddressStreet} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-mailingAddressCity">{language === 'es' ? 'Ciudad' : 'City'}</label>
                <input id="intake-mailingAddressCity" type="text" name="mailingAddressCity" className="cc-input" value={formData.mailingAddressCity} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-mailingAddressState">{language === 'es' ? 'Estado' : 'State'}</label>
                <select id="intake-mailingAddressState" name="mailingAddressState" className="cc-select" value={formData.mailingAddressState} onChange={handleChange}>
                  <option value="">—</option>
                  {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-mailingAddressZip">{language === 'es' ? 'Código postal' : 'Zip code'}</label>
                <input id="intake-mailingAddressZip" type="text" name="mailingAddressZip" className="cc-input" value={formData.mailingAddressZip} onChange={handleChange} />
              </div>
            </div>
          )}
        </div>

        {formData.propertyType !== 'commercial' && (
          <>
            {showOwner2 && renderOwner('owner2', language === 'es' ? 'Asegurado 2' : 'Insured 2', () => { setShowOwner2(false); setShowOwner3(false); setFormData(prev => ({ ...prev, owner2: emptyOwner, owner3: emptyOwner })); })}
            {showOwner3 && renderOwner('owner3', language === 'es' ? 'Asegurado 3' : 'Insured 3', () => { setShowOwner3(false); setFormData(prev => ({ ...prev, owner3: emptyOwner })); })}
            {!showOwner3 && (
              <button type="button" className="cc-add-card" onClick={() => (showOwner2 ? setShowOwner3(true) : setShowOwner2(true))}>
                <i className="fa-solid fa-plus"></i> {showOwner2
                  ? (language === 'es' ? 'Agregar asegurado 3' : 'Add insured 3')
                  : (language === 'es' ? 'Agregar asegurado 2' : 'Add insured 2')}
              </button>
            )}
          </>
        )}

        <details className="cc-optional" open={openContacts} onToggle={event => setOpenContacts(event.currentTarget.open)}>
        <summary>{language === 'es' ? 'Otros contactos (no asegurados) · opcional' : 'Other contacts (not insured) · optional'}
          {formData.additionalContacts.length > 0 && <span className="cc-optional-count">{formData.additionalContacts.length}</span>}
        </summary>
        {formData.additionalContacts.map((c, i) => (
          <div className="cc-person-card" key={i}>
            <div className="cc-person-card-title">
              <i className="fa-solid fa-address-book"></i> {language === 'es' ? `Contacto ${i + 1}` : `Contact ${i + 1}`}
              <button type="button" className="cc-owner-remove" onClick={() => removeContact(i)}><i className="fa-solid fa-xmark"></i> {language === 'es' ? 'Quitar' : 'Remove'}</button>
            </div>
            <div className="cc-grid">
              <div className="cc-input-group">
                <label className="cc-label" htmlFor={`contact-${i}-name`}>{language === 'es' ? 'Nombre completo' : 'Full name'}</label>
                <input id={`contact-${i}-name`} type="text" className="cc-input" value={c.name} onChange={e => setContact(i, 'name', e.target.value)} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor={`contact-${i}-relation`}>{language === 'es' ? 'Relación' : 'Relation'}</label>
                <input id={`contact-${i}-relation`} type="text" className="cc-input" placeholder={language === 'es' ? 'ej. hija, inquilino, administrador' : 'e.g. daughter, tenant, manager'} value={c.relation} onChange={e => setContact(i, 'relation', e.target.value)} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor={`contact-${i}-phone`}>{language === 'es' ? 'Teléfono' : 'Phone'}</label>
                <input id={`contact-${i}-phone`} type="tel" className="cc-input" value={c.phone} onChange={e => setContact(i, 'phone', e.target.value)} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor={`contact-${i}-email`}>{language === 'es' ? 'Correo' : 'Email'}</label>
                <input id={`contact-${i}-email`} type="email" className="cc-input" value={c.email} onChange={e => setContact(i, 'email', e.target.value)} />
              </div>
            </div>
          </div>
        ))}
        <button type="button" className="cc-add-card" onClick={addContact}><i className="fa-solid fa-plus"></i> {language === 'es' ? 'Agregar contacto' : 'Add contact'}</button>
        </details>

        <p className="cc-subsection-label">{language === 'es' ? 'La pérdida' : 'The loss'}</p>
        <div className="cc-grid">
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-incidentDate"><i className="fa-solid fa-calendar-day"></i> Date of Loss *</label>
            <input id="intake-incidentDate" type="date" name="incidentDate" value={formData.incidentDate} onChange={handleChange} required className="cc-input" />
          </div>
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-lossTime"><i className="fa-solid fa-clock"></i> {language === 'es' ? 'Hora aproximada' : 'Approx. time'}</label>
            <input id="intake-lossTime" type="time" name="lossTime" value={formData.lossTime} onChange={handleChange} className="cc-input" />
          </div>
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-cause"><i className="fa-solid fa-triangle-exclamation"></i> {language === 'es' ? 'Causa' : 'Cause'} *</label>
            <div className="cc-select-with-config">
              <select id="intake-cause" name="cause" value={formData.cause} onChange={handleChange} required className="cc-select">
                <option value="">{language === 'es' ? 'Seleccione una causa' : 'Select a cause'}</option>
                {causesList.map((cause, idx) => <option key={idx} value={cause}>{cause}</option>)}
              </select>
              {isAdmin && (
                <button type="button" onClick={() => { setEditingCause(null); setNewCauseName(''); setShowCauseConfigModal(true); }} className="cc-btn-config"><i className="fa-solid fa-gear"></i></button>
              )}
            </div>
          </div>
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-claimType">{language === 'es' ? 'Tipo de reclamo' : 'Claim type'}</label>
            <select id="intake-claimType" name="claimType" value={formData.claimType} onChange={handleChange} className="cc-select">
              <option value="">—</option>
              <option value="emergency">Emergency</option>
              <option value="non_emergency">Non Emergency</option>
              <option value="supplemental">Supplemental</option>
            </select>
          </div>
        </div>
        <div className="cc-input-group">
          <label className="cc-label" htmlFor="intake-lossDescription">{language === 'es' ? 'Descripción breve de la pérdida' : 'Brief description of loss'}</label>
          <input id="intake-lossDescription" type="text" name="lossDescription" value={formData.lossDescription} onChange={handleChange} className="cc-input" placeholder={language === 'es' ? 'ej. Wind damage to roof, water intrusion in kitchen' : 'e.g. Wind damage to roof, water intrusion in kitchen'} />
        </div>

        <p className="cc-subsection-label">{language === 'es' ? 'Asignación' : 'Assignment'}</p>
        <div className="cc-grid">
          {/* El ajustador trabaja siempre dentro de su propia empresa (ya viene
              asignada al iniciar sesión) — el selector de Empresa solo lo ve el
              equipo CSR, que trabaja con varias (reunión 22-sep). */}
          {(isAdmin || isCsr) && (
            <div className="cc-input-group">
              <label className="cc-label" htmlFor="intake-companyName"><i className="fa-solid fa-building"></i> {language === 'es' ? 'Empresa' : 'Company'} *</label>
              <select id="intake-companyName" name="companyName" value={formData.companyName} onChange={handleChange} required className="cc-select">
                {companiesList.map((company, idx) => <option key={idx} value={company}>{company}</option>)}
              </select>
            </div>
          )}
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-adjusterName"><i className="fa-solid fa-user-tie"></i> {language === 'es' ? 'P.A Assigned Adjuster (principal · comisión)' : 'P.A Assigned Adjuster (main · commission)'} *</label>
            {canPickAdjuster || (isPaAssistant && !existingRecord) ? (
              <select id="intake-adjusterName" name="adjusterName" value={formData.adjusterName} onChange={handleChange} required className="cc-select" disabled={pickableAdjusters.length === 0}>
                <option value="">{pickableAdjusters.length === 0 ? 'No adjusters' : (language === 'es' ? 'Selecciona un ajustador' : 'Select an adjuster')}</option>
                {pickableAdjusters.map((adjuster, idx) => <option key={idx} value={adjuster}>{adjuster}</option>)}
              </select>
            ) : (
              <input id="intake-adjusterName" type="text" value={formData.adjusterName} disabled className="cc-input" />
            )}
          </div>
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-agreedFeePercent"><i className="fa-solid fa-percent"></i> {language === 'es' ? '% de honorario acordado con el cliente' : 'Fee % agreed with client'}</label>
            <input id="intake-agreedFeePercent" type="number" min="0" max="100" step="0.5" name="agreedFeePercent" value={formData.agreedFeePercent} onChange={handleChange} className="cc-input" placeholder="ej. 15" />
          </div>
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-claimNumber"><i className="fa-solid fa-hashtag"></i> {language === 'es' ? 'Número de reclamo (si ya lo asignó el seguro)' : 'Claim number (if already assigned)'}</label>
            <input id="intake-claimNumber" type="text" name="claimNumber" value={formData.claimNumber} onChange={handleChange} className="cc-input" placeholder="ej. CDP00202667" />
          </div>
          {isAdmin && (
            <div className="cc-input-group">
              <label className="cc-label" htmlFor="intake-oaStatus"><i className="fa-solid fa-tag"></i> {language === 'es' ? 'Estado (Organized)' : 'Status (Organized)'}</label>
              <select id="intake-oaStatus" name="oaStatus" value={formData.oaStatus} onChange={handleChange} className="cc-input">
                <option value="">{language === 'es' ? 'Sin estado' : 'No status'}</option>
                {formData.oaStatus && !statusCatalog.some(item => item.name.toLowerCase() === formData.oaStatus.toLowerCase()) && <option value={formData.oaStatus}>{formData.oaStatus}</option>}
                {statusCatalog.map(item => <option key={item.id} value={item.name}>{item.name}</option>)}
              </select>
            </div>
          )}
        </div>

        <details className="cc-optional" open={openExtras} onToggle={event => setOpenExtras(event.currentTarget.open)}>
        <summary>{language === 'es' ? 'Acceso, abogado y fechas · opcional' : 'Access, attorney and dates · optional'}</summary>
        {/* El lead siempre es el PA principal asignado: no se pregunta. Al crear tampoco se piden estos datos; se completan al editar. */}
        {existingRecord && (
          <>
        <p className="cc-subsection-label">{language === 'es' ? 'Personas del reclamo' : 'People on the claim'}</p>
        <div className="cc-grid">
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-signatureObtainedBy">{language === 'es' ? 'Quién obtuvo la firma' : 'Signature obtained by'}</label>
            <input id="intake-signatureObtainedBy" type="text" name="signatureObtainedBy" className="cc-input" value={formData.signatureObtainedBy} onChange={handleChange} />
          </div>
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-negotiator">{language === 'es' ? 'Negociador' : 'Negotiator'}</label>
            <input id="intake-negotiator" type="text" name="negotiator" className="cc-input" value={formData.negotiator} onChange={handleChange} />
          </div>
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-mitigationCompany">{language === 'es' ? 'Compañía de mitigación' : 'Mitigation company'}</label>
            <input id="intake-mitigationCompany" type="text" name="mitigationCompany" className="cc-input" value={formData.mitigationCompany} onChange={handleChange} />
          </div>
        </div>
          </>
        )}

        {/* Personas con acceso al reclamo (utils/claimAccess.js): otros PA
            asignados, Other Adjuster Access (asistentes y colaboradores) y el
            abogado (por ahora solo el nombre, sin cuenta). */}
        <p className="cc-subsection-label">{language === 'es' ? 'P.A Assigned Adjuster — otros PA asignados' : 'P.A Assigned Adjuster — other assigned PAs'}</p>
        <div className="cc-collaborator-list">
          {/* Un ajustador solo ve/asigna colaboradores de SU PROPIA empresa
              — no tiene por qué conocer a los de otras empresas. */}
          {getAdjustersForCompanyObjects(formData.companyName).filter(a => a.name !== formData.adjusterName).map(a => (
            <label key={a.id || a.name} className="cc-checkbox-row cc-collaborator-chip">
              <input type="checkbox" disabled={!canEditAccess} checked={formData.collaboratorAdjusters.includes(a.name)} onChange={() => toggleCollaborator(a.name)} />
              {a.name}
            </label>
          ))}
          {getAdjustersForCompanyObjects(formData.companyName).filter(a => a.name !== formData.adjusterName).length === 0 && (
            <span className="cc-subsection-label">{language === 'es' ? 'No hay otros ajustadores disponibles.' : 'No other adjusters available.'}</span>
          )}
        </div>

        {(() => {
          const assigned = [formData.adjusterName, ...formData.collaboratorAdjusters].filter(Boolean);
          const linked = assistantsFor(assigned, adjustersList);
          const pickable = adjustersList.filter(a => a.company === formData.companyName && isAssistant(a) && !linked.includes(a.name));
          return (
            <>
              <p className="cc-subsection-label">Other Adjuster Access</p>
              <div className="cc-collaborator-list">
                {linked.map(name => (
                  <span key={name} className="cc-checkbox-row cc-collaborator-chip" title={language === 'es' ? 'Asistente enlazado al perfil de un PA asignado' : 'Assistant linked to an assigned PA'}>
                    <i className="fa-solid fa-link"></i> {name}
                  </span>
                ))}
                {pickable.map(a => (
                  <label key={a.id || a.name} className="cc-checkbox-row cc-collaborator-chip">
                    <input type="checkbox" disabled={!canEditAccess} checked={formData.otherAccess.includes(a.name)} onChange={() => toggleIn('otherAccess', a.name)} />
                    {a.name}
                  </label>
                ))}
                {linked.length === 0 && pickable.length === 0 && (
                  <span className="cc-subsection-label">
                    {language === 'es' ? 'Sin asistentes. Se crean en Ajustadores y se enlazan al perfil de su PA.' : 'No assistants. Create them in Adjusters and link them to their PA.'}
                  </span>
                )}
              </div>
            </>
          );
        })()}

        <div className="cc-grid">
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-attorneyName"><i className="fa-solid fa-scale-balanced"></i> Attorney</label>
            <input id="intake-attorneyName" type="text" name="attorneyName" className="cc-input" value={formData.attorneyName} onChange={handleChange} placeholder={language === 'es' ? 'Nombre del abogado o firma' : 'Attorney or firm name'} />
          </div>
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-contractSignedDate">{language === 'es' ? 'Contrato firmado' : 'Contract signed'}</label>
            <input id="intake-contractSignedDate" type="date" name="contractSignedDate" className="cc-input" value={formData.contractSignedDate} onChange={handleChange} />
          </div>
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-dateCalledIn">{language === 'es' ? 'Fecha en que llamó' : 'Date called in'}</label>
            <input id="intake-dateCalledIn" type="date" name="dateCalledIn" className="cc-input" value={formData.dateCalledIn} onChange={handleChange} />
          </div>
          <div className="cc-input-group">
            <label className="cc-label" htmlFor="intake-vendor">Vendor</label>
            <input id="intake-vendor" type="text" name="vendor" className="cc-input" value={formData.vendor} onChange={handleChange} placeholder={language === 'es' ? 'Restauradora que refirió' : 'Referring restoration company'} />
          </div>
        </div>
        </details>

        {/* ── PASO 2: PÓLIZA Y SEGURO (se puede completar después) ── */}
        {/* Al CREAR no se pide: es un paso posterior, desde la ficha (Editar reclamo). */}
        {existingRecord ? (
          <div className="cc-section-toggle">
            <button type="button" className="cc-btn-pill" onClick={() => setShowPolicySection(v => !v)}>
              <i className={`fa-solid fa-chevron-${showPolicySection ? 'up' : 'down'}`}></i>
              {language === 'es' ? '2. Póliza y seguro' : '2. Policy & insurance'}
            </button>
          </div>
        ) : (
          <p className="cc-next-step-note" role="note">
            <i className="fa-solid fa-circle-info" aria-hidden="true"></i>{' '}
            {language === 'es'
              ? 'La aseguradora, la póliza, las coberturas y la hipotecaria se completan después de crear el reclamo, desde su ficha (Editar reclamo).'
              : 'Insurance, policy, coverages and mortgage are completed after the claim is created, from its page (Edit claim).'}
          </p>
        )}

        {showPolicySection && (
          <>
            <h3 className="cc-section-title"><i className="fa-solid fa-shield-halved"></i> {language === 'es' ? 'Aseguradora y póliza' : 'Insurance & policy'}</h3>
            <div className="cc-grid">
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-insuranceCompanyName">{language === 'es' ? 'Aseguradora' : 'Insurance company'}</label>
                <input id="intake-insuranceCompanyName" type="text" name="insuranceCompanyName" value={formData.insuranceCompanyName} onChange={handleChange} className="cc-input" placeholder="ej. Citizens Property Insurance" />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-policyNumber">{language === 'es' ? 'Número de póliza' : 'Policy number'}</label>
                <input id="intake-policyNumber" type="text" name="policyNumber" value={formData.policyNumber} onChange={handleChange} className="cc-input" placeholder="ej. FL-0112466" />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-policyEffectiveDate">{language === 'es' ? 'Vigencia desde' : 'Effective date'}</label>
                <input id="intake-policyEffectiveDate" type="date" name="policyEffectiveDate" className="cc-input" value={formData.policyEffectiveDate} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-policyExpirationDate">{language === 'es' ? 'Vigencia hasta' : 'Expiration date'}</label>
                <input id="intake-policyExpirationDate" type="date" name="policyExpirationDate" className="cc-input" value={formData.policyExpirationDate} onChange={handleChange} />
              </div>
            </div>
            <div className="cc-grid">
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-insuranceCompanyAddress">{language === 'es' ? 'Dirección de la aseguradora' : 'Insurance company address'}</label>
                <input id="intake-insuranceCompanyAddress" type="text" name="insuranceCompanyAddress" className="cc-input" value={formData.insuranceCompanyAddress} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-insuranceClaimEmail">{language === 'es' ? 'Correo de reclamos (aseguradora)' : 'Claims email (insurance)'}</label>
                <input id="intake-insuranceClaimEmail" type="email" name="insuranceClaimEmail" className="cc-input" value={formData.insuranceClaimEmail} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-insurancePhone">{language === 'es' ? 'Teléfono general (aseguradora)' : 'General phone (insurance)'}</label>
                <input id="intake-insurancePhone" type="tel" name="insurancePhone" className="cc-input" value={formData.insurancePhone} onChange={handleChange} />
              </div>
            </div>
            <div className="cc-grid">
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-occupancy">{language === 'es' ? 'Ocupación de la propiedad' : 'Occupancy'}</label>
                <input id="intake-occupancy" type="text" name="occupancy" className="cc-input" value={formData.occupancy} onChange={handleChange} placeholder="ej. Owner-occupied primary residence" />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-insuredInterest">{language === 'es' ? 'Interés del asegurado' : 'Insured interest'}</label>
                <input id="intake-insuredInterest" type="text" name="insuredInterest" className="cc-input" value={formData.insuredInterest} onChange={handleChange} placeholder={language === 'es' ? 'Vacío = según el tipo de póliza (HO → Owner, DP → Tenant)' : 'Empty = from the policy form (HO → Owner, DP → Tenant)'} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-crmClaimId"><i className="fa-solid fa-link"></i> {language === 'es' ? 'ID del Reclamo (CRM)' : 'Claim ID (CRM)'}</label>
                <input id="intake-crmClaimId" type="text" name="crmClaimId" value={formData.crmClaimId} onChange={handleChange} placeholder="Ej: 53063" className="cc-input" />
              </div>
            </div>

            <p className="cc-subsection-label">{language === 'es' ? 'Agente de seguros (quien vendió la póliza)' : 'Insurance agent (who sold the policy)'}</p>
            <div className="cc-grid">
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-agentName">{language === 'es' ? 'Nombre del agente' : 'Agent name'}</label>
                <input id="intake-agentName" type="text" name="agentName" className="cc-input" value={formData.agentName} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-agentAgency">{language === 'es' ? 'Agencia' : 'Agency'}</label>
                <input id="intake-agentAgency" type="text" name="agentAgency" className="cc-input" value={formData.agentAgency} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-agentPhone">{language === 'es' ? 'Teléfono del agente' : 'Agent phone'}</label>
                <input id="intake-agentPhone" type="tel" name="agentPhone" className="cc-input" value={formData.agentPhone} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-agentAddress">{language === 'es' ? 'Dirección de la agencia' : 'Agency address'}</label>
                <input id="intake-agentAddress" type="text" name="agentAddress" className="cc-input" value={formData.agentAddress} onChange={handleChange} />
              </div>
            </div>

            <p className="cc-subsection-label">{language === 'es' ? 'Field Adjuster (FA) — hace la inspección' : 'Field Adjuster (FA) — inspects'}</p>
            <div className="cc-grid">
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-insuranceAdjusterName">{language === 'es' ? 'Nombre' : 'Name'}</label>
                <input id="intake-insuranceAdjusterName" type="text" name="insuranceAdjusterName" className="cc-input" value={formData.insuranceAdjusterName} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-insuranceAdjusterPhone">{language === 'es' ? 'Teléfono' : 'Phone'}</label>
                <input id="intake-insuranceAdjusterPhone" type="tel" name="insuranceAdjusterPhone" className="cc-input" value={formData.insuranceAdjusterPhone} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-insuranceAdjusterEmail">{language === 'es' ? 'Correo' : 'Email'}</label>
                <input id="intake-insuranceAdjusterEmail" type="email" name="insuranceAdjusterEmail" className="cc-input" value={formData.insuranceAdjusterEmail} onChange={handleChange} />
              </div>
            </div>
            <p className="cc-subsection-label">{language === 'es' ? 'Desk Adjuster (DA) — decide la cobertura' : 'Desk Adjuster (DA) — decides coverage'}</p>
            <div className="cc-grid">
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-deskAdjusterName">{language === 'es' ? 'Nombre' : 'Name'}</label>
                <input id="intake-deskAdjusterName" type="text" name="deskAdjusterName" className="cc-input" value={formData.deskAdjusterName} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-deskAdjusterPhone">{language === 'es' ? 'Teléfono' : 'Phone'}</label>
                <input id="intake-deskAdjusterPhone" type="tel" name="deskAdjusterPhone" className="cc-input" value={formData.deskAdjusterPhone} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-deskAdjusterEmail">{language === 'es' ? 'Correo' : 'Email'}</label>
                <input id="intake-deskAdjusterEmail" type="email" name="deskAdjusterEmail" className="cc-input" value={formData.deskAdjusterEmail} onChange={handleChange} />
              </div>
            </div>

            <h3 className="cc-section-title"><i className="fa-solid fa-file-invoice-dollar"></i> {language === 'es' ? 'Coberturas y deducible' : 'Coverages & deductible'}</h3>
            <div className="cc-grid">
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-coverageA">{language === 'es' ? 'Cobertura A (Estructura)' : 'Coverage A (Dwelling)'}</label>
                <input id="intake-coverageA" type="number" name="coverageA" className="cc-input" value={formData.coverageA} onChange={handleChange} placeholder="$" />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-coverageB">{language === 'es' ? 'Cobertura B (Otras estructuras)' : 'Coverage B (Other structures)'}</label>
                <input id="intake-coverageB" type="number" name="coverageB" className="cc-input" value={formData.coverageB} onChange={handleChange} placeholder="$" />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-coverageC">{language === 'es' ? 'Cobertura C (Contenido)' : 'Coverage C (Contents)'}</label>
                <input id="intake-coverageC" type="number" name="coverageC" className="cc-input" value={formData.coverageC} onChange={handleChange} placeholder="$" />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-coverageD">{language === 'es' ? 'Cobertura D (Pérdida de uso)' : 'Coverage D (Loss of use)'}</label>
                <input id="intake-coverageD" type="number" name="coverageD" className="cc-input" value={formData.coverageD} onChange={handleChange} placeholder="$" />
              </div>
            </div>
            <div className="cc-grid">
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-deductibleAmount">{language === 'es' ? 'Deducible' : 'Deductible'}</label>
                <input id="intake-deductibleAmount" type="number" name="deductibleAmount" className="cc-input" value={formData.deductibleAmount} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-deductibleType">{language === 'es' ? 'Tipo de deducible' : 'Deductible type'}</label>
                <select id="intake-deductibleType" name="deductibleType" className="cc-select" value={formData.deductibleType} onChange={handleChange}>
                  <option value="fixed">$ {language === 'es' ? 'Fijo' : 'Fixed'}</option>
                  <option value="percentage">% {language === 'es' ? 'Porcentaje (sobre Cobertura A)' : 'Percentage (of Coverage A)'}</option>
                </select>
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-recoverableDepreciation">{language === 'es' ? 'Depreciación recuperable (RD) del estimado' : 'Recoverable Depreciation (RD) from the estimate'}</label>
                <input id="intake-recoverableDepreciation" type="number" name="recoverableDepreciation" className="cc-input" value={formData.recoverableDepreciation} onChange={handleChange} placeholder="$" />
              </div>
            </div>

            <h3 className="cc-section-title"><i className="fa-solid fa-building-columns"></i> {language === 'es' ? 'Compañía hipotecaria' : 'Mortgage company'}</h3>
            <div className="cc-grid">
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-mortgageCompanyName">{language === 'es' ? 'Nombre del banco/mortgage' : 'Mortgage/bank name'}</label>
                <input id="intake-mortgageCompanyName" type="text" name="mortgageCompanyName" className="cc-input" value={formData.mortgageCompanyName} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-mortgageLoanNumber">{language === 'es' ? 'Número de préstamo' : 'Loan number'}</label>
                <input id="intake-mortgageLoanNumber" type="text" name="mortgageLoanNumber" className="cc-input" value={formData.mortgageLoanNumber} onChange={handleChange} />
              </div>
              <div className="cc-input-group">
                <label className="cc-label" htmlFor="intake-mortgagePhone">{language === 'es' ? 'Teléfono del mortgage' : 'Mortgage phone'}</label>
                <input id="intake-mortgagePhone" type="tel" name="mortgagePhone" className="cc-input" value={formData.mortgagePhone} onChange={handleChange} />
              </div>
            </div>
          </>
        )}

        <div className="cc-form-footer">
          <button type="submit" className="cc-btn-submit" disabled={loading}>
            {loading ? <><i className="fa-solid fa-spinner fa-spin"></i> ...</> : existingRecord
              ? <><i className="fa-solid fa-check"></i> {language === 'es' ? 'Guardar cambios' : 'Save changes'}</>
              : <><i className="fa-solid fa-check"></i> {language === 'es' ? 'Crear reclamo' : 'Create claim'}</>}
          </button>
        </div>
      </form>

      {showCauseConfigModal && (
        <div className="cc-modal-overlay" onClick={() => setShowCauseConfigModal(false)}>
          <div className="cc-modal-content cc-modal-cause" onClick={(e) => e.stopPropagation()}>
            <div className="cc-modal-header">
              <h3><i className="fa-solid fa-triangle-exclamation"></i> {language === 'es' ? 'Configurar Causas' : 'Configure Causes'}</h3>
              <button className="cc-modal-close" onClick={() => setShowCauseConfigModal(false)}><i className="fa-solid fa-times"></i></button>
            </div>
            <div className="cc-modal-body">
              <div className="cc-config-list">
                {causesList.map(c => (
                  <div key={c} className="cc-config-item">
                    {editingCause === c ? (
                      <div className="cc-config-edit">
                        <input type="text" value={newCauseName} onChange={e => setNewCauseName(e.target.value)} className="cc-modal-input cc-config-input" autoFocus />
                        <button type="button" onClick={handleSaveCauseEdit} className="cc-config-btn-save"><i className="fa-solid fa-check"></i></button>
                        <button type="button" onClick={() => setEditingCause(null)} className="cc-config-btn-cancel"><i className="fa-solid fa-times"></i></button>
                      </div>
                    ) : (
                      <>
                        <span className="cc-config-item-name">{c}</span>
                        <div className="cc-config-item-actions">
                          <button type="button" onClick={() => { setEditingCause(c); setNewCauseName(c); }} className="cc-config-btn-edit"><i className="fa-solid fa-pen"></i></button>
                          <button type="button" onClick={() => handleDeleteCause(c)} className="cc-config-btn-delete" disabled={causesList.length <= 1}><i className="fa-solid fa-trash"></i></button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              {!editingCause && (
                <div className="cc-config-add">
                  <input type="text" value={newCauseName} onChange={e => setNewCauseName(e.target.value)} placeholder="New cause..." className="cc-modal-input cc-config-input" />
                  <button type="button" onClick={handleAddCause} className="cc-config-btn-add"><i className="fa-solid fa-plus"></i> {language === 'es' ? 'Agregar' : 'Add'}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!pendingDeleteCause}
        icon="fa-trash"
        danger
        title={language === 'es' ? 'Eliminar causa' : 'Delete cause'}
        message={pendingDeleteCause ? (language === 'es' ? `¿Eliminar la causa "${pendingDeleteCause}"?` : `Delete cause "${pendingDeleteCause}"?`) : ''}
        confirmLabel={language === 'es' ? 'Eliminar' : 'Delete'}
        cancelLabel={language === 'es' ? 'Cancelar' : 'Cancel'}
        onConfirm={confirmDeleteCause}
        onCancel={() => setPendingDeleteCause(null)}
      />
    </div>
  );
};

export default CreateClaimRecord;
